import { useMemo, useState } from "react";
import { Upload, Button, message, List, Progress } from "antd";
import { UploadOutlined, DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { downloadFile, downloadLocalFile } from "utils";

const FileUpload = ({
  fileList: initialFileList = [],
  setFileList,
  acceptedTypes = "application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*",
  maxSize = 20,
  maxFiles = 5,
  label = "Upload",
  disablePreview = false,
  viewOnly = false, // Added viewOnly prop
}) => {
  const fileListState = useMemo(() => initialFileList, [initialFileList]);
  const [previews, setPreviews] = useState([]);
  const [downloadStates, setDownloadStates] = useState({});

  const handleRemove = (file) => {
    const index = fileListState.indexOf(file);
    const newFileList = fileListState.slice();
    newFileList.splice(index, 1);
    setFileList(newFileList);
    const newPreviews = previews.slice();
    newPreviews.splice(index, 1);
    setPreviews(newPreviews);
  };

  const handleDownload = async (file, index) => {
    try {
      if (typeof file !== "string") {
        downloadLocalFile(file);
      } else {
        setDownloadStates(prev => ({ ...prev, [index]: { downloading: true, progress: 0 } }));
        
        await downloadFile(
          file,
          typeof file === "string" ? file.substring(file.lastIndexOf("/") + 1) : file?.name,
          (progress) => {
            setDownloadStates(prev => ({ 
              ...prev, 
              [index]: { downloading: true, progress } 
            }));
          }
        );
        
        setDownloadStates(prev => ({ ...prev, [index]: { downloading: false, progress: 100 } }));
        
        setTimeout(() => {
          setDownloadStates(prev => {
            const newState = { ...prev };
            delete newState[index];
            return newState;
          });
        }, 1000);
      }
    } catch (error) {
      message.error("Failed to download file. Please try again.");
      setDownloadStates(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    }
  };

  const handleBeforeUpload = (file) => {
    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      message.error(`File must be smaller than ${maxSize}MB!`);
      return false;
    }

    const existingFile = fileListState.find(
      (f) => f.name === file.name && f.size === file.size
    );
    if (existingFile) {
      message.error(`${file.name} is already added.`);
      return false;
    }

    if (fileListState.length >= maxFiles) {
      message.error(`You can only upload up to ${maxFiles} files.`);
      return false;
    }

    if (!disablePreview) {
      const previewUrl = URL.createObjectURL(file);
      setPreviews([...previews, previewUrl]);
    }

    const newFileList = [...fileListState, file];
    setFileList(newFileList);

    return false;
  };

  const imagesProps = {
    onRemove: handleRemove,
    beforeUpload: handleBeforeUpload,
    fileList: fileListState,
    accept: acceptedTypes,
    showUploadList: false,
  };

  return (
    <>
      {!viewOnly && (
        <Upload {...imagesProps} className="min-w-[200px] w-full">
          <Button className="w-full" icon={<UploadOutlined />}>
            {label}
          </Button>
        </Upload>
      )}
      {!viewOnly && fileListState.length > 0 ? (
        <List
          dataSource={fileListState}
          className="max-h-[130px] overflow-auto"
          renderItem={(file, index) => {
            const downloadState = downloadStates[index];
            const isDownloading = downloadState?.downloading;
            const progress = downloadState?.progress || 0;

            return (
            <List.Item
              actions={[
                !viewOnly && (
                  <Button
                    size="small"
                    danger
                    onClick={() => handleRemove(file)}
                      disabled={isDownloading}
                  >
                    Remove
                  </Button>
                ),
                  <Button 
                    size="small" 
                    onClick={() => handleDownload(file, index)}
                    disabled={isDownloading}
                    icon={isDownloading ? <LoadingOutlined /> : <DownloadOutlined />}
                  >
                    {isDownloading ? `${progress}%` : 'Download'}
                </Button>,
              ]}
            >
                <div className="flex-1">
              <List.Item.Meta
                avatar={
                  !disablePreview && previews[index] ? (
                    <img
                      src={previews[index]}
                      alt={file.name}
                      className="rounded-[100%] h-[50px] w-[50px]"
                    />
                  ) : null
                }
                title={
                  typeof file === "string"
                    ? file.substring(file.lastIndexOf("/") + 1)
                    : file?.name
                }
              />
                  {isDownloading && (
                    <Progress 
                      percent={progress} 
                      size="small" 
                      status="active"
                      strokeColor="#7ED957"
                      showInfo={false}
                    />
                  )}
                </div>
            </List.Item>
            );
          }}
        />
      ) : null}
    </>
  );
};

export default FileUpload;
